import { ChangeDetectionStrategy, Component, HostBinding, Inject, Input, OnChanges, Optional } from '@angular/core';

import { RegionVisibilityService } from '@frontend/sports/betting-offer/feature/region-visibility';
import { FavouritesConfig, GridConfig as GridConfigs } from '@frontend/sports/common/client-config-data-access';
import { SPORTS_WITHOUT_AVAILABLE_REGION, SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { ColoredIconComponent } from '@frontend/sports/common/ui/colored-icon';
import { CompetitionLogoComponent } from '@frontend/sports/competition-logos/feature';
import { FavouriteLeagueToggleComponent } from '@frontend/sports/favourites/feature/league-toggle';
import { GRID_CONFIG, GridConfig } from '@frontend/sports/grid/core/feature/config';
import { LeagueGroup } from '@frontend/sports/grid/core/feature/model';
import { includes } from 'lodash-es';

@Component({
    selector: 'ms-league-header',
    templateUrl: 'league-header.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FavouriteLeagueToggleComponent, ColoredIconComponent, CompetitionLogoComponent],
})
export class LeagueHeaderComponent implements OnChanges {
    @Input() league: LeagueGroup;
    @Input() sportId: number;
    @Input() hideFavourites = false;
    @Input() useColoredIcon = false;
    @Input() hideHeaderRegion = false;

    @Input() set competitionFallbackIcon(value: string | undefined) {
        this.fallbackIcon = value || 'sports-icon sports-league';
    }

    @HostBinding('class') className = 'league-group';

    fallbackIcon: string;
    showCompetitionLogo = false;
    showPipeSeparator = this.gridConfigs.gridEventDisplayVersion !== 'V2';

    constructor(
        @Optional() @Inject(GRID_CONFIG) private gridConfig: GridConfig | null,
        private regionVisibilityService: RegionVisibilityService,
        private gridConfigs: GridConfigs,
        private favouritesConfig: FavouritesConfig,
    ) {}

    ngOnChanges(): void {
        if (this.hideHeaderRegion) {
            this.showCompetitionLogo = true;
        }
    }

    get showFavouriteToggle(): boolean {
        return this.league.canBeFavourited && !this.hideFavourites && this.favouritesConfig.isFavouritesEnabled;
    }

    get title(): string {
        if (this.hideHeaderRegion) {
            return this.league.name;
        }
        const hidePrefix = includes(SPORTS_WITHOUT_AVAILABLE_REGION, this.sportId);

        if (hidePrefix) {
            return this.league.name;
        } else {
            const region =
                this.gridConfig && this.gridConfig.useRegionCode ? (this.league.region.code ?? this.league.region.name) : this.league.region.name;

            const leagueName = this.showPipeSeparator ? `${region} | ${this.league.name}` : `${region} • ${this.league.name}`;

            return this.regionVisibilityService.shouldHide(this.league.region, this.league) ? this.league.name : leagueName;
        }
    }

    get iconClass(): string {
        return this.isTournament ? 'sports-tournament' : `country-icon c${this.league.region.id}`;
    }

    get isTournament(): boolean {
        return this.sportId === SportConstant.Tennis;
    }
}
